import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowDown, Clock, Users, Award, Star, Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EmbedPathwayProps {
  compact?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  maxWidth?: string;
}

const EmbedPathway = ({ 
  compact = false, 
  showHeader = true, 
  showFooter = true,
  maxWidth = "4xl"
}: EmbedPathwayProps) => {
  const certifications = [
    {
      id: "csce",
      name: "CSCE",
      fullName: "Converged Security & Cybersecurity Expert",
      level: "Foundational Certification",
      description: "Essential cybersecurity knowledge and practical skills for integrated physical and digital security environments.",
      prerequisites: "No prior cybersecurity experience required",
      timeEstimate: "Program duration + final assessment",
      cost: "€250",
      validity: "3 years",
      badge: "./images/csce.png",
      color: "from-pink-500 to-pink-600"
    },
    {
      id: "csp",
      name: "CSP",
      fullName: "Converged Security Professional",
      level: "Advanced Certification",
      description: "Strategic insight and operational leadership for cross-functional teams and enterprise-wide security initiatives.",
      prerequisites: "CSCE certification required",
      timeEstimate: "On-site small group format + group presentation",
      cost: "€750",
      validity: "3 years",
      badge: "./images/csp.png",
      color: "from-blue-600 to-blue-700"
    },
    {
      id: "csl",
      name: "CSL",
      fullName: "Converged Security Leader",
      level: "Leadership Credential",
      description: "Highest credential recognizing advanced expertise, strategic insight, and operational readiness for enterprise leadership.",
      prerequisites: "Both CSCE and CSP certifications",
      timeEstimate: "Awarded automatically upon completion of prerequisites",
      cost: "No additional fee",
      validity: "3 years",
      badge: "./images/leader.png",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      id: "ccsp",
      name: "C-CSP",
      fullName: "Chartered Converged Security Practitioner",
      level: "Premier / Chartered Designation",
      description: "Elite-level leadership, strategic integration and operational excellence in implementing enterprise-wide converged security frameworks.",
      prerequisites: "CSCE + CSP + CSL + 5 years leadership experience + framework implementation",
      timeEstimate: "Application process + implementation verification",
      cost: "€500 (€50 application + €450 final)",
      validity: "3 years (Chartered Award)",
      badge: null,
      color: "from-purple-600 to-purple-700"
    }
  ];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50 font-sans relative">
      {/* Navigation Bar - Upper Right */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
        >
          <Home className="w-4 h-4 mr-1" />
          Home
        </Button>
      </div>
      {/* Header */}
      {showHeader && (
        <div className="bg-gradient-to-r from-slate-800 to-blue-900 text-white py-8">
          <div className={`max-w-${maxWidth} mx-auto px-4 text-center`}>
            <h1 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold mb-2`}>
              CSI Certification Pathway
            </h1>
            <p className={`${compact ? 'text-base' : 'text-lg'} text-blue-100 max-w-2xl mx-auto`}>
              Progressive structure designed to develop expertise from foundational knowledge to chartered practitioner level
            </p>
          </div>
        </div>
      )}

      {/* Certification Pathway */}
      <div className={`max-w-${maxWidth} mx-auto px-4 ${showHeader ? 'py-8' : 'py-4'}`}>
        <div className="relative">
          {certifications.map((cert, index) => (
            <div key={cert.id} className={`relative ${compact ? 'mb-4' : 'mb-6'}`}>
              {/* Certification Card */}
              <Card className="relative overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${cert.color}`}></div>
                
                <CardHeader className={compact ? "pb-2" : "pb-3"}>
                  <div className={`flex items-start gap-${compact ? '3' : '4'}`}>
                    {/* Badge Image */}
                    <div className="flex-shrink-0">
                      {cert.badge ? (
                        <img 
                          src={cert.badge} 
                          alt={`${cert.name} Badge`}
                          className={`${compact ? 'w-12 h-12' : 'w-16 h-16'} object-contain`}
                        />
                      ) : (
                        <div className={`${compact ? 'w-12 h-12' : 'w-16 h-16'} rounded-full bg-gradient-to-br ${cert.color} flex items-center justify-center`}>
                          <Award className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-white`} />
                        </div>
                      )}
                    </div>
                    
                    {/* Certification Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-slate-800`}>
                          {cert.name}
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {cert.level}
                        </Badge>
                      </div>
                      <CardDescription className={`${compact ? 'text-sm' : 'text-base'} font-medium text-slate-600 mb-1`}>
                        {cert.fullName}
                      </CardDescription>
                      {!compact && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {cert.description}
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className={`grid ${compact ? 'grid-cols-2' : 'md:grid-cols-2'} gap-3 text-xs`}>
                    {/* Prerequisites */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 font-medium text-slate-700">
                        <Users className="w-3 h-3" />
                        Prerequisites
                      </div>
                      <p className="text-slate-600 pl-4 leading-tight">
                        {cert.prerequisites}
                      </p>
                    </div>

                    {/* Cost */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 font-medium text-slate-700">
                        <Star className="w-3 h-3" />
                        Cost
                      </div>
                      <p className="text-slate-600 pl-4 font-medium">
                        {cert.cost}
                      </p>
                    </div>

                    {!compact && (
                      <>
                        {/* Time Estimate */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 font-medium text-slate-700">
                            <Clock className="w-3 h-3" />
                            Format & Duration
                          </div>
                          <p className="text-slate-600 pl-4 leading-tight">
                            {cert.timeEstimate}
                          </p>
                        </div>

                        {/* Validity */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 font-medium text-slate-700">
                            <Award className="w-3 h-3" />
                            Validity
                          </div>
                          <p className="text-slate-600 pl-4">
                            {cert.validity}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Arrow Connector */}
              {index < certifications.length - 1 && (
                <div className={`flex justify-center ${compact ? 'my-3' : 'my-4'}`}>
                  <ArrowDown className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-slate-400`} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Information */}
        {showFooter && (
          <div className={`${compact ? 'mt-6 p-4' : 'mt-8 p-5'} bg-gradient-to-r from-slate-800 to-blue-900 rounded-lg text-white`}>
            <h3 className={`${compact ? 'text-base' : 'text-lg'} font-bold mb-3`}>Key Information</h3>
            <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'md:grid-cols-2 gap-3'} text-xs`}>
              <div>
                <h4 className="font-semibold mb-1">Certification Maintenance</h4>
                <p className="text-blue-100">40 hours continuing education (3-year period)</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Instructor</h4>
                <p className="text-blue-100">Dr. Vladimir Bunic, PhD in Cybersecurity</p>
              </div>
              {!compact && (
                <>
                  <div>
                    <h4 className="font-semibold mb-1">Format</h4>
                    <p className="text-blue-100">Digital certificates and badges, verifiable</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Compliance</h4>
                    <p className="text-blue-100">EU GDPR, Cybersecurity Act, NIS Directive</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbedPathway;